// src/vatandas/pages/VatandasKentHaritasi.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import vatandasApi from "../services/vatandasApi";
import { useDispatch, useSelector } from "react-redux";

import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  CircleMarker,
  Polyline,
  Polygon,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import "./KentHaritasi.css";
import { fetchImportantPlaces } from "../redux/slices/vatandasImportantPlacesSlice";

import {
  Squares2X2Icon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  MapPinIcon,
  ArrowsPointingOutIcon,
  PencilSquareIcon, //   ölçüm ikonu (eski ArrowsRightLeftIcon yerine)
  TrashIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

// Leaflet default marker icon fix
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/* =============================================================================
  Config
============================================================================= */

// === Ayarlanabilir varsayılanlar ===
const MAP_DEFAULT_CENTER = [36.44639122225149, 35.94071112336046]; // yeni merkez (lat, lng)
const MAP_DEFAULT_ZOOM = 12; // 12–15 arası
const MAX_BOUNDS_DELTA = 0.18;
// merkezin etrafında kare sınır (~8-9 km)

// bounds'u merkeze göre otomatik üret
function buildBoundsFromCenter(center, delta) {
  const [lat, lng] = center;
  return [
    [lat - delta, lng - delta],
    [lat + delta, lng + delta],
  ];
}

const MAP_MAX_BOUNDS = buildBoundsFromCenter(MAP_DEFAULT_CENTER, MAX_BOUNDS_DELTA);


// localStorage key
const STORAGE_KEY_ACTIVE = "vmap_active_layer_keys_v2";

/* =============================================================================
  Helpers
============================================================================= */

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function escapeHtml(str) {
  const s = String(str ?? "");
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizePlacesPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}

function getPlaceId(place) {
  const id = place?.id ?? place?.pk;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

function getPlaceName(place) {
  return String(place?.name || place?.title || "Katman");
}

/**
 *   Layer toggle bug fix:
 * - Active key MUST be stable even if id null.
 * - Use: `id:<num>` or `name:<slugged>`
 */
function getPlaceKey(place) {
  const id = getPlaceId(place);
  if (id != null) return `id:${id}`;

  const name = getPlaceName(place).trim().toLowerCase();
  // stable-ish fallback
  const safe = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `name:${safe || "katman"}`;
}

function getPlaceColor(place) {
  return String(place?.color || "#007cc2");
}

function formatDistanceMeters(meters) {
  const m = Number(meters || 0);
  if (!Number.isFinite(m) || m <= 0) return "-";
  if (m < 1000) return `${Math.round(m)} m`;
  const km = m / 1000;
  return `${km.toFixed(km < 10 ? 2 : 1)} km`;
}

function formatAreaSqMeters(m2) {
  const a = Number(m2 || 0);
  if (!Number.isFinite(a) || a <= 0) return "-";
  if (a < 10_000) return `${Math.round(a)} m²`;
  const ha = a / 10_000;
  if (ha < 100) return `${ha.toFixed(2)} ha`;
  const km2 = a / 1_000_000;
  return `${km2.toFixed(2)} km²`;
}

/**
 * GeoJSON normalize:
 * - Accepts: FeatureCollection | Feature | Geometry
 * - Returns: { nonPointFC, pointsLatLngs }
 */
function splitGeoJson(raw) {
  if (!raw) return { nonPointFC: null, pointsLatLngs: [] };

  let features = [];

  if (raw.type === "FeatureCollection" && Array.isArray(raw.features)) {
    features = raw.features.filter(Boolean);
  } else if (raw.type === "Feature" && raw.geometry) {
    features = [raw];
  } else if (raw.type && raw.coordinates) {
    features = [{ type: "Feature", properties: {}, geometry: raw }];
  } else if (raw.geometry && raw.type) {
    features = [{ type: "Feature", properties: raw.properties || {}, geometry: raw.geometry }];
  } else {
    return { nonPointFC: null, pointsLatLngs: [] };
  }

  const nonPointFeatures = [];
  const pointsLatLngs = [];

  const pushPoint = (coords) => {
    if (!Array.isArray(coords) || coords.length < 2) return;
    const lng = safeNumber(coords[0]);
    const lat = safeNumber(coords[1]);
    if (lat == null || lng == null) return;
    pointsLatLngs.push([lat, lng]);
  };

  const pushMultiPoints = (coordsList) => {
    if (!Array.isArray(coordsList)) return;
    for (const c of coordsList) pushPoint(c);
  };

  for (const f of features) {
    const g = f?.geometry;
    const t = g?.type;

    if (!g || !t) continue;

    if (t === "Point") {
      pushPoint(g.coordinates);
      continue;
    }
    if (t === "MultiPoint") {
      pushMultiPoints(g.coordinates);
      continue;
    }

    nonPointFeatures.push(f);
  }

  const nonPointFC =
    nonPointFeatures.length > 0 ? { type: "FeatureCollection", features: nonPointFeatures } : null;

  return { nonPointFC, pointsLatLngs };
}

/**
 * Bounds for any geometry (recursive walk)
 */
function collectLatLngsFromGeoJson(raw) {
  const out = [];

  const walkCoords = (coords) => {
    if (!Array.isArray(coords)) return;

    // [lng, lat]
    if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
      const lng = safeNumber(coords[0]);
      const lat = safeNumber(coords[1]);
      if (lat != null && lng != null) out.push([lat, lng]);
      return;
    }

    for (const c of coords) walkCoords(c);
  };

  const addGeometry = (geom) => {
    if (!geom?.type || geom.coordinates == null) return;
    walkCoords(geom.coordinates);
  };

  if (!raw) return out;

  if (raw.type === "FeatureCollection" && Array.isArray(raw.features)) {
    for (const f of raw.features) addGeometry(f?.geometry);
    return out;
  }

  if (raw.type === "Feature") {
    addGeometry(raw.geometry);
    return out;
  }

  if (raw.type && raw.coordinates) {
    addGeometry(raw);
    return out;
  }

  if (raw.geometry) {
    addGeometry(raw.geometry);
    return out;
  }

  return out;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(max-width: 768px)")?.matches ?? false;
  });

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 768px)");
    if (!mq) return;

    const onChange = (e) => setIsMobile(!!e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

function getLeafletMap(mapRef) {
  const m = mapRef?.current;
  if (!m) return null;

  if (typeof m.fitBounds === "function") return m;
  if (m.leafletElement && typeof m.leafletElement.fitBounds === "function") return m.leafletElement;

  return null;
}

/* =============================================================================
  Measurement math (no external plugin)
  - Distance: polyline sum
  - Area: spherical polygon area + perimeter
============================================================================= */

const EARTH_RADIUS_M = 6378137;

function deg2rad(d) {
  return (d * Math.PI) / 180;
}

function distanceMeters(a, b) {
  return L.latLng(a[0], a[1]).distanceTo(L.latLng(b[0], b[1]));
}

function polylineLengthMeters(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < points.length; i++) sum += distanceMeters(points[i - 1], points[i]);
  return sum;
}

function polygonPerimeterMeters(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let sum = 0;
  for (let i = 1; i < points.length; i++) sum += distanceMeters(points[i - 1], points[i]);
  // close ring
  sum += distanceMeters(points[points.length - 1], points[0]);
  return sum;
}

// Spherical polygon area (approx) similar to Leaflet.Draw GeometryUtil.geodesicArea
function polygonAreaSqMeters(latLngs) {
  if (!Array.isArray(latLngs) || latLngs.length < 3) return 0;

  let area = 0;
  for (let i = 0, j = latLngs.length - 1; i < latLngs.length; j = i++) {
    const p1 = latLngs[i];
    const p2 = latLngs[j];

    const lat1 = deg2rad(p1[0]);
    const lat2 = deg2rad(p2[0]);
    const dLng = deg2rad(p1[1] - p2[1]);

    area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  area = (area * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2;
  return Math.abs(area);
}

/* =============================================================================
  Leaflet Events for measurement
============================================================================= */

function MeasureEvents({ enabled, mode, onAddPoint }) {
  useMapEvents({
    click: (e) => {
      if (!enabled) return;
      onAddPoint([e.latlng.lat, e.latlng.lng], mode);
    },
  });
  return null;
}

/* =============================================================================
  Toast (upper-right)
============================================================================= */

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon = toast.type === "error" ? XMarkIcon : MapPinIcon;

  return (
    <div className={`vmap-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="vmap-toast-ic" aria-hidden="true" />
      <div className="vmap-toast-body">
        <div className="vmap-toast-title">{toast.title}</div>
        <div className="vmap-toast-text">{toast.message}</div>
      </div>
      <button className="vmap-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="vmap-toast-close-ic" aria-hidden="true" />
      </button>
    </div>
  );
}

/* =============================================================================
  Page
============================================================================= */

export default function VatandasKentHaritasi() {
  const dispatch = useDispatch();
  const { placesList, status } = useSelector((state) => state.vatandasImportantPlaces);

  const mapRef = useRef(null);

  // Kent haritası başlangıç merkezi/zoom'u İdari Sınırlar'dan (PUBLIC endpoint) → farklı
  // belediyede doğru şehir; boundary sonradan gelince mapRef ile imperatif ortala.
  const [boundaryView, setBoundaryView] = useState(null);
  useEffect(() => {
    let iptal = false;
    vatandasApi
      .get("/bm4/boundaries/default-view/")
      .then((res) => {
        if (iptal) return;
        const lat = Number(res?.data?.center?.lat);
        const lng = Number(res?.data?.center?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const z = Number(res?.data?.zoom) || null;
          setBoundaryView({ center: [lat, lng], zoom: z });
          const m = mapRef.current;
          if (m) m.setView([lat, lng], z ?? m.getZoom(), { animate: false });
        }
      })
      .catch(() => {});
    return () => {
      iptal = true;
    };
  }, []);

  const [isLayersOpen, setIsLayersOpen] = useState(false);

  //   store keys as string -> fixes "layer appears but doesn't toggle" issues
  const [activeLayerKeys, setActiveLayerKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ACTIVE);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((x) => String(x)).filter(Boolean);
    } catch {
      return [];
    }
  });

  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);

  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);

  // Measurement
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measureMode, setMeasureMode] = useState("distance"); // "distance" | "area"
  const [measurePoints, setMeasurePoints] = useState([]); // latlng[]
  const isMobile = useIsMobile();

  const places = useMemo(() => normalizePlacesPayload(placesList), [placesList]);

  // Scroll lock for fullscreen
  useEffect(() => {
    document.body.classList.add("vmap-body");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.classList.remove("vmap-body");
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Fetch
  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchImportantPlaces());
    }
  }, [dispatch, status]);

  // Persist active layers
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_ACTIVE, JSON.stringify(activeLayerKeys));
    } catch {}
  }, [activeLayerKeys]);

  // Toast auto close
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ESC closes panels + measurement
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLayersOpen(false);
        setIsMeasuring(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
  }, []);

  const filteredPlaces = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return places;
    return places.filter((p) => getPlaceName(p).toLowerCase().includes(q));
  }, [places, query]);

  const activePlaces = useMemo(() => {
    if (!activeLayerKeys.length) return [];
    const setKeys = new Set(activeLayerKeys);
    return places.filter((p) => setKeys.has(getPlaceKey(p)));
  }, [places, activeLayerKeys]);

  const activeRenderData = useMemo(() => {
    return activePlaces.map((place) => {
      const { nonPointFC, pointsLatLngs } = splitGeoJson(place?.geojson_data);
      return { place, nonPointFC, pointsLatLngs };
    });
  }, [activePlaces]);

  const setLayerActive = useCallback((place, nextActive) => {
    const key = getPlaceKey(place);

    setActiveLayerKeys((prev) => {
      const has = prev.includes(key);
      if (nextActive && !has) return [...prev, key];
      if (!nextActive && has) return prev.filter((k) => k !== key);
      return prev;
    });
  }, []);

  const clearAllLayers = useCallback(() => {
    setActiveLayerKeys([]);
  }, []);

  const enableAllFiltered = useCallback(() => {
    const keys = filteredPlaces.map(getPlaceKey);
    setActiveLayerKeys((prev) => {
      const setPrev = new Set(prev);
      for (const k of keys) setPrev.add(k);
      return Array.from(setPrev);
    });
  }, [filteredPlaces]);

  const fitToActiveLayers = useCallback(() => {
    const map = getLeafletMap(mapRef);
    if (!map) return;

    const latLngs = [];

    for (const place of activePlaces) {
      try {
        const coords = collectLatLngsFromGeoJson(place?.geojson_data);
        for (const c of coords) latLngs.push(c);

        const { pointsLatLngs } = splitGeoJson(place?.geojson_data);
        for (const p of pointsLatLngs) latLngs.push(p);
      } catch {}
    }

    if (!latLngs.length) {
  map.flyTo(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM, { duration: 0.6 });
  return;
}


    if (latLngs.length === 1) {
      map.flyTo(latLngs[0], Math.max(map.getZoom(), 16), { duration: 0.6 });
      return;
    }

    const b = L.latLngBounds(latLngs);
    map.fitBounds(b.pad(0.12), { animate: true });
  }, [activePlaces]);

  const locateMe = useCallback(() => {
    if (!navigator?.geolocation) {
      showToast("error", "Konum", "Tarayıcı konumu desteklemiyor.");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = safeNumber(pos?.coords?.latitude);
        const lng = safeNumber(pos?.coords?.longitude);
        if (lat == null || lng == null) {
          setIsLocating(false);
          showToast("error", "Konum", "Konum alınamadı.");
          return;
        }

        const next = { lat, lng };
        setUserLocation(next);

        const map = getLeafletMap(mapRef);
        if (map) map.flyTo([lat, lng], Math.max(map.getZoom(), 16), { duration: 0.7 });

        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
        showToast("error", "Konum", "Konum izni verilmedi veya alınamadı.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
  }, [showToast]);

  const geoJsonStyle = useCallback((place) => {
    const color = getPlaceColor(place);
    return { color, weight: 2, opacity: 1, fillOpacity: 0.22 };
  }, []);

  const onEachFeature = useCallback((place) => {
    return (feature, layer) => {
      const placeName = escapeHtml(getPlaceName(place));
      const fName = feature?.properties?.name ? escapeHtml(feature.properties.name) : "";
      const fDesc = feature?.properties?.description ? escapeHtml(feature.properties.description) : "";

      const title = fName || placeName;
      const desc = fDesc ? `<div class="vmap-popup-desc">${fDesc}</div>` : "";

      const html = `<div class="vmap-popup">
        <div class="vmap-popup-title">${title}</div>
        ${desc}
      </div>`;

      layer.bindPopup(html, { closeButton: true });
    };
  }, []);

  /* =============================================================================
    Measurement behavior
    - distance: polyline points (>=2 gives total length)
    - area: polygon points (>=3 gives area + perimeter)
  ============================================================================= */

  const toggleMeasure = useCallback(() => {
    setIsMeasuring((prev) => {
      const next = !prev;
      if (!next) setMeasurePoints([]);
      return next;
    });
  }, []);

  const clearMeasure = useCallback(() => {
    setMeasurePoints([]);
  }, []);

  const undoMeasure = useCallback(() => {
    setMeasurePoints((prev) => prev.slice(0, -1));
  }, []);

  const finishMeasure = useCallback(() => {
    // no-op: we keep points, just stop capturing clicks
    setIsMeasuring(false);
  }, []);

  const setMode = useCallback((mode) => {
    setMeasureMode(mode);
    setMeasurePoints([]); // mode change -> reset
    setIsMeasuring(true); // user intent: start measuring immediately
  }, []);

  const addMeasurePoint = useCallback((latlng, mode) => {
    setMeasurePoints((prev) => {
      const next = Array.isArray(prev) ? [...prev, latlng] : [latlng];

      // keep distance tool manageable (optional cap)
      if (mode === "distance" && next.length > 200) return next.slice(-200);

      // area: cap as well
      if (mode === "area" && next.length > 300) return next.slice(-300);

      return next;
    });
  }, []);

  const measureDistanceMeters = useMemo(() => {
    if (measureMode !== "distance") return 0;
    return polylineLengthMeters(measurePoints);
  }, [measureMode, measurePoints]);

  const measurePerimeterMeters = useMemo(() => {
    if (measureMode !== "area") return 0;
    return polygonPerimeterMeters(measurePoints);
  }, [measureMode, measurePoints]);

  const measureAreaSqMeters = useMemo(() => {
    if (measureMode !== "area") return 0;
    return polygonAreaSqMeters(measurePoints);
  }, [measureMode, measurePoints]);

  const measureHint = useMemo(() => {
    if (!isMeasuring) return "";
    if (measureMode === "distance") {
      if (measurePoints.length === 0) return "Çizgi ölçümü: haritaya dokunarak nokta ekleyin.";
      if (measurePoints.length === 1) return "Devam edin: ikinci noktayı seçin.";
      return "Devam edin: daha fazla nokta ekleyebilir, Bitir ile tamamlayabilirsiniz.";
    }
    // area
    if (measurePoints.length === 0) return "Alan ölçümü: poligon çizmek için nokta ekleyin.";
    if (measurePoints.length < 3) return "Alan için en az 3 nokta gerekir.";
    return "Alan/çevre hazır: nokta ekleyebilir veya Bitir ile tamamlayabilirsiniz.";
  }, [isMeasuring, measureMode, measurePoints.length]);

  const onOpenLayers = useCallback(() => {
    setIsLayersOpen(true);
  }, []);

  const loading = status === "loading";
  const failed = status === "failed";

  return (
    <div className="vmap-page" data-mobile={isMobile ? "1" : "0"}>
      <div className="vmap-map">
        {loading ? <div className="vmap-loading">Yükleniyor...</div> : null}
        {failed ? <div className="vmap-loading">Katmanlar alınamadı.</div> : null}

        <MapContainer
  center={boundaryView?.center || MAP_DEFAULT_CENTER}
  zoom={boundaryView?.zoom || MAP_DEFAULT_ZOOM}     //   ilk açılış daha geriden

  //   kullanıcı daha rahat zoom-out yapabilsin
  minZoom={Math.max(1, MAP_DEFAULT_ZOOM - 4)} // örn: 12 -> 8
  maxZoom={19}

  scrollWheelZoom={true}
  zoomControl={true}

  //   bounds artık merkeze göre üretildi
  maxBounds={MAP_MAX_BOUNDS}

  //   1.0 çok sert: kenara dayanınca “gri/itme” hissi artar
  maxBoundsViscosity={0.35}

  // ölçümde double click zoom kapalı kalsın
  doubleClickZoom={!isMeasuring}

  whenCreated={(map) => {
    mapRef.current = map;
  }}
  style={{ height: "100%", width: "100%" }}
>


          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          {/* Measurement click capture */}
          <MeasureEvents enabled={isMeasuring} mode={measureMode} onAddPoint={addMeasurePoint} />

          {/* Measurement visuals */}
          {isMeasuring || measurePoints.length ? (
            <>
              {measureMode === "distance" ? (
                <>
                  {measurePoints.length >= 2 ? (
                    <Polyline
                      positions={measurePoints}
                      pathOptions={{
                        color: "#007cc2",
                        weight: 3,
                        opacity: 0.9,
                        dashArray: "6 6",
                      }}
                    >
                      <Tooltip direction="center" opacity={1} permanent>
                        {formatDistanceMeters(measureDistanceMeters)}
                      </Tooltip>
                    </Polyline>
                  ) : null}

                  {measurePoints.map((p, idx) => (
                    <CircleMarker
                      key={`md-pt-${idx}`}
                      center={p}
                      radius={7}
                      pathOptions={{ color: "#007cc2", weight: 2, fillOpacity: 1, opacity: 1 }}
                    />
                  ))}
                </>
              ) : (
                <>
                  {measurePoints.length >= 3 ? (
                    <Polygon
                      positions={measurePoints}
                      pathOptions={{
                        color: "#007cc2",
                        weight: 2,
                        opacity: 0.9,
                        fillOpacity: 0.18,
                      }}
                    >
                      <Tooltip direction="center" opacity={1} permanent>
                        {formatAreaSqMeters(measureAreaSqMeters)}
                      </Tooltip>
                    </Polygon>
                  ) : null}

                  {measurePoints.length >= 2 ? (
                    <Polyline
                      positions={measurePoints}
                      pathOptions={{
                        color: "#007cc2",
                        weight: 2,
                        opacity: 0.75,
                        dashArray: "6 6",
                      }}
                    />
                  ) : null}

                  {measurePoints.map((p, idx) => (
                    <CircleMarker
                      key={`ma-pt-${idx}`}
                      center={p}
                      radius={7}
                      pathOptions={{ color: "#007cc2", weight: 2, fillOpacity: 1, opacity: 1 }}
                    />
                  ))}
                </>
              )}
            </>
          ) : null}

          {/* Active layers */}
          {activeRenderData.map(({ place, nonPointFC, pointsLatLngs }) => {
            const key = getPlaceKey(place);
            const color = getPlaceColor(place);

            return (
              <React.Fragment key={key}>
                {nonPointFC ? (
                  <GeoJSON
                    data={nonPointFC}
                    style={() => geoJsonStyle(place)}
                    onEachFeature={onEachFeature(place)}
                  />
                ) : null}

                {/* Points as circle markers for better visibility */}
                {pointsLatLngs.map((pos, idx) => (
                  <CircleMarker
                    key={`${key}-pt-${idx}`}
                    center={pos}
                    radius={8}
                    pathOptions={{ color, weight: 2, fillOpacity: 1, opacity: 1 }}
                  >
                    <Popup>
                      <div className="vmap-popup-react">
                        <div className="vmap-popup-title">{getPlaceName(place)}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </React.Fragment>
            );
          })}

          {/* User location marker */}
          {userLocation ? (
            <Marker position={[userLocation.lat, userLocation.lng]}>
              <Popup>Konumunuz</Popup>
            </Marker>
          ) : null}
        </MapContainer>

        {/* Title pill */}
        <div className="vmap-title v-card" aria-label="Harita Başlığı">
          <MapPinIcon className="vmap-title-ic" aria-hidden="true" />
          <div className="vmap-title-text">
            <div className="vmap-title-main">Kent Haritası</div>
            <div className="vmap-title-sub">
              {activeLayerKeys.length}/{places.length} katman
            </div>
          </div>
        </div>

        {/* Measure card */}
        {(isMeasuring || measurePoints.length) && (
          <div className="vmap-measure v-card" role="status" aria-live="polite">
            <div className="vmap-measure-head">
              <div className="vmap-measure-title">
                <PencilSquareIcon className="vmap-measure-ic" aria-hidden="true" />
                Ölçüm
              </div>

              <div className="vmap-measure-actions">
                <button type="button" className="vmap-mini-btn" onClick={undoMeasure} disabled={!measurePoints.length}>
                  <ArrowUturnLeftIcon className="vmap-mini-ic" aria-hidden="true" />
                  Geri
                </button>

                <button type="button" className="vmap-mini-btn" onClick={clearMeasure} disabled={!measurePoints.length}>
                  <TrashIcon className="vmap-mini-ic" aria-hidden="true" />
                  Temizle
                </button>

                <button type="button" className="vmap-mini-btn" onClick={finishMeasure} disabled={!isMeasuring}>
                  <CheckCircleIcon className="vmap-mini-ic" aria-hidden="true" />
                  Bitir
                </button>

                <button type="button" className="vmap-mini-btn" onClick={toggleMeasure}>
                  <XMarkIcon className="vmap-mini-ic" aria-hidden="true" />
                  Kapat
                </button>
              </div>
            </div>

            <div className="vmap-measure-body">
              <div className="vmap-measure-hint">{measureHint}</div>

              <div className="vmap-measure-result">
                <div className="vmap-measure-tabs" role="tablist" aria-label="Ölçüm modu">
                  <button
                    type="button"
                    className={`vmap-tab ${measureMode === "distance" ? "is-active" : ""}`}
                    onClick={() => setMode("distance")}
                    role="tab"
                    aria-selected={measureMode === "distance"}
                  >
                    Mesafe
                  </button>
                  <button
                    type="button"
                    className={`vmap-tab ${measureMode === "area" ? "is-active" : ""}`}
                    onClick={() => setMode("area")}
                    role="tab"
                    aria-selected={measureMode === "area"}
                  >
                    Alan
                  </button>
                </div>

                {measureMode === "distance" ? (
                  <div className="vmap-measure-metrics">
                    <div className="vmap-measure-metric">
                      <div className="vmap-measure-label">Toplam Mesafe</div>
                      <div className="vmap-measure-value">
                        {measurePoints.length >= 2 ? formatDistanceMeters(measureDistanceMeters) : "-"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="vmap-measure-metrics">
                    <div className="vmap-measure-metric">
                      <div className="vmap-measure-label">Alan</div>
                      <div className="vmap-measure-value">
                        {measurePoints.length >= 3 ? formatAreaSqMeters(measureAreaSqMeters) : "-"}
                      </div>
                    </div>

                    <div className="vmap-measure-metric">
                      <div className="vmap-measure-label">Çevre</div>
                      <div className="vmap-measure-value">
                        {measurePoints.length >= 3 ? formatDistanceMeters(measurePerimeterMeters) : "-"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Floating controls */}
        <div className="vmap-controls" role="group" aria-label="Harita Kontrolleri">
          <button
            type="button"
            className="vmap-fab"
            onClick={onOpenLayers}
            aria-label="Katmanları aç"
            aria-expanded={isLayersOpen ? "true" : "false"}
          >
            <Squares2X2Icon className="vmap-fab-ic" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="vmap-fab"
            onClick={locateMe}
            aria-label="Konumuma git"
            disabled={isLocating}
            title="Konum"
          >
            {isLocating ? (
              <ArrowPathIcon className="vmap-fab-ic spin" aria-hidden="true" />
            ) : (
              <svg className="vmap-fab-ic" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M11 2h2v2.05A8.001 8.001 0 0 1 19.95 11H22v2h-2.05A8.001 8.001 0 0 1 13 19.95V22h-2v-2.05A8.001 8.001 0 0 1 4.05 13H2v-2h2.05A8.001 8.001 0 0 1 11 4.05V2Zm1 4a6 6 0 1 0 .001 12.001A6 6 0 0 0 12 6Zm0 3a3 3 0 1 1 0 6a3 3 0 0 1 0-6Z"
                />
              </svg>
            )}
          </button>

          <button
            type="button"
            className={`vmap-fab ${isMeasuring ? "is-active" : ""}`}
            onClick={() => {
              // ölçüm ilk kez açılıyorsa mevcut moda göre başlat
              if (!isMeasuring) setIsMeasuring(true);
              else setIsMeasuring(false);
            }}
            aria-label="Ölçüm aracı"
            title="Ölçüm"
          >
            <PencilSquareIcon className="vmap-fab-ic" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="vmap-fab"
            onClick={fitToActiveLayers}
            aria-label="Aktif katmanlara sığdır"
            title="Sığdır"
          >
            <ArrowsPointingOutIcon className="vmap-fab-ic" aria-hidden="true" />
          </button>
        </div>

        {/* Layers panel/sheet */}
        {isLayersOpen ? (
          <>
            <div className="vmap-overlay" onClick={() => setIsLayersOpen(false)} aria-hidden="true" />

            <div
              className={isMobile ? "vmap-sheet v-card" : "vmap-panel v-card"}
              role="dialog"
              aria-modal="true"
              aria-label="Katmanlar"
            >
              <div className="vmap-panel-head">
                <div className="vmap-panel-title">Katmanlar</div>

                <button
                  type="button"
                  className="vmap-panel-x"
                  onClick={() => setIsLayersOpen(false)}
                  aria-label="Kapat"
                >
                  <XMarkIcon className="vmap-panel-x-ic" aria-hidden="true" />
                </button>
              </div>

              <div className="vmap-panel-search">
                <MagnifyingGlassIcon className="vmap-panel-search-ic" aria-hidden="true" />
                <input
                  className="vmap-panel-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Katman ara"
                />
              </div>

              <div className="vmap-panel-actions">
                <button type="button" className="vmap-mini-btn" onClick={enableAllFiltered}>
                  Tümünü Aç
                </button>
                <button type="button" className="vmap-mini-btn" onClick={clearAllLayers}>
                  Tümünü Kapat
                </button>
              </div>

              <div className="vmap-panel-list" role="list">
                {filteredPlaces.map((place) => {
                  const key = getPlaceKey(place);
                  const name = getPlaceName(place);
                  const color = getPlaceColor(place);
                  const isActive = activeLayerKeys.includes(key);

                  return (
                    <div key={key} className="vmap-layer-row" role="listitem">
                      <div className="vmap-layer-left">
                        <span className="vmap-layer-swatch" style={{ backgroundColor: color }} />
                        <span className="vmap-layer-name">{name}</span>
                      </div>

                      <label className="vmap-switch" aria-label={`${name} katmanını ${isActive ? "kapat" : "aç"}`}>
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={(e) => setLayerActive(place, e.target.checked)}
                        />
                        <span className="vmap-switch-ui" aria-hidden="true" />
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="vmap-panel-note">Katmanlar anında uygulanır.</div>
            </div>
          </>
        ) : null}

        {/* Toast */}
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    </div>
  );
}
